import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "build-ts/metadata";
import { HeaderWithStats } from "services/MetadataServices";
import { buildWorkflowTag } from "modules/chat/utils/chatUtils";
import { ScheduledJobProto, ScheduledScenarioJobProto } from "build-ts/schedule";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
import {
  setScheduledJobIsActive,
  deleteScheduledJob,
  getScheduledJob,
  putScheduledJob,
  shareScheduledJob,
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import ConfirmationModal from "common/modals/ConfirmationModal";
import ShareWorkflowModal from "common/modals/ShareWorkflowModal";
import JobList from "modules/analyst/components/JobList";

interface JobListContainerProps {
  jobs: Header[];
  isLoadingJobs: boolean;
  analystsList?: HeaderWithStats[];
  onJobClick?: (job: Header) => void;
  onDefinitionClick?: (job: Header) => void;
  onPastRunsClick?: (job: Header) => void;
  onRefresh?: () => void;
}

const JobListContainer: React.FC<JobListContainerProps> = ({
  jobs,
  isLoadingJobs,
  analystsList,
  onJobClick,
  onDefinitionClick,
  onPastRunsClick,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { userId } = useUser();
  const [copyingJobId, setCopyingJobId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Header | null>(null);
  const [jobToShare, setJobToShare] = useState<Header | null>(null);
  const [shareSelectedIds, setShareSelectedIds] = useState<string[]>([]);
  const [isLoadingShareDetails, setIsLoadingShareDetails] = useState(false);
  const [isSavingShare, setIsSavingShare] = useState(false);
  const [sharedByMap, setSharedByMap] = useState<Record<string, string[]>>({});

  const loadSharedByMap = useCallback(async (jobList: Header[]) => {
    const jobsWithIds = jobList.filter(job => job.id);
    if (jobsWithIds.length === 0) {
      setSharedByMap({});
      return;
    }

    const entries = await Promise.all(
      jobsWithIds.map(async job => {
        const details = await getScheduledJob(job.id!, false);
        return [job.id!, details?.shared_by ?? []] as const;
      })
    );
    setSharedByMap(Object.fromEntries(entries));
  }, []);

  useEffect(() => {
    if (isLoadingJobs) {
      return;
    }
    loadSharedByMap(jobs);
  }, [jobs, isLoadingJobs, loadSharedByMap]);

  useEffect(() => {
    if (!jobToShare?.id) {
      setShareSelectedIds([]);
      return;
    }

    let cancelled = false;
    const loadShareDetails = async () => {
      setIsLoadingShareDetails(true);
      const details = await getScheduledJob(jobToShare.id!, false);
      if (!cancelled) {
        setShareSelectedIds(details?.shared_by ?? []);
        setIsLoadingShareDetails(false);
      }
    };
    loadShareDetails();

    return () => {
      cancelled = true;
    };
  }, [jobToShare]);

  const handleToggleActive = async (job: Header, isActive: boolean) => {
    if (!job.id) return;
    const response = await setScheduledJobIsActive(job.id, isActive);
    if (response) {
      showToast(
        isActive ? "Workflow set to Live" : "Workflow paused",
        `"${job.name}" is now ${isActive ? "live" : "paused"}.`,
        "success"
      );
      onRefresh?.();
    }
  };

  const handleRunJob = async (job: Header) => {
    // Job details are needed to get the darwin name because LLM needs the darwin name to run the
    // workflow.
    const jobDetails = await getScheduledJob(job.id!, false);
    if (!jobDetails) {
      return;
    }
    navigate("/chat/new", {
      state: {
        initialPrompt:
          "I want to run the workflow:" +
          `${buildWorkflowTag(jobDetails.scheduled_analysis?.darwin_name!, job.name!)}.` +
          " Fetch the details of the workflow and run it. Ask me If there is any input required to run the workflow.",
        analystId: job.owner_id,
      },
    });
  };

  const handleCopyJob = async (job: Header) => {
    if (!job.id) return;
    setCopyingJobId(job.id);
    try {
      const jobDetails = await getScheduledJob(job.id, false);
      if (!jobDetails) {
        showToast("Failed to fetch workflow details", "", "error");
        return;
      }

      const currentDarwinName = jobDetails.scheduled_analysis?.darwin_name?.trim() || "";
      const randomSuffix = Math.random().toString(36).slice(2, 8);
      const duplicatedScheduledAnalysis = ScheduledScenarioJobProto.create({
        ...jobDetails.scheduled_analysis,
        darwin_name: `${currentDarwinName}_${randomSuffix}`,
      });

      const newJob = ScheduledJobProto.create({
        header: Header.create({
          id: uuidv4(),
          name: `${jobDetails.header?.name || "Untitled"} (Copy)`,
          type: MetadataTypeEnum.SCHEDULED_JOB,
          owner_id: jobDetails.header?.owner_id,
        }),
        cron_expr: jobDetails.cron_expr,
        type: jobDetails.type,
        is_active: false,
        scheduled_analysis: duplicatedScheduledAnalysis,
      });

      const response = await putScheduledJob(newJob);
      if (response) {
        showToast("Workflow duplicated", `"${newJob.header?.name}" has been created.`, "success");
        onRefresh?.();
      } else {
        showToast("Failed to duplicate", "", "error");
      }
    } catch {
      showToast("Failed to duplicate", "", "error");
    } finally {
      setCopyingJobId(null);
    }
  };

  const handleDeleteClick = (job: Header) => {
    setJobToDelete(job);
  };

  const handleShareClick = (job: Header) => {
    setJobToShare(job);
  };

  const handleShareClose = () => {
    setJobToShare(null);
  };

  const handleShareSave = async (selectedAnalystIds: string[]) => {
    if (!jobToShare?.id) return;
    setIsSavingShare(true);
    try {
      const response = await shareScheduledJob(jobToShare.id, selectedAnalystIds);
      if (response) {
        const sharedAnalystNames = selectedAnalystIds
          .map(id => analystsList?.find(a => a.id === id)?.name)
          .filter((name): name is string => !!name);
        const shareDetail =
          sharedAnalystNames.length > 0
            ? `Shared with ${sharedAnalystNames.join(", ")}.`
            : `Shared with ${response.shared_by.length} analyst${response.shared_by.length === 1 ? "" : "s"}.`;
        showToast("Workflow shared", shareDetail, "success");
        setSharedByMap(prev => ({
          ...prev,
          [jobToShare.id!]: response.shared_by,
        }));
        setJobToShare(null);
        onRefresh?.();
      }
    } finally {
      setIsSavingShare(false);
    }
  };

  const handleDeleteCancel = () => {
    setJobToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete?.id) return;
    setIsDeleting(true);
    const response = await deleteScheduledJob(jobToDelete.id);
    setIsDeleting(false);
    if (response) {
      showToast("Workflow deleted", `"${jobToDelete.name}" has been deleted.`, "success");
      setJobToDelete(null);
      onRefresh?.();
    }
  };

  return (
    <>
      <JobList
        jobs={jobs}
        isLoadingJobs={isLoadingJobs}
        userId={userId}
        copyingJobId={copyingJobId}
        isDeleting={isDeleting}
        sharedByMap={sharedByMap}
        analystsList={analystsList}
        onJobClick={onJobClick}
        onDefinitionClick={onDefinitionClick}
        onPastRunsClick={onPastRunsClick}
        onToggleActive={handleToggleActive}
        onRunJob={handleRunJob}
        onCopyJob={handleCopyJob}
        onShareClick={handleShareClick}
        onDeleteClick={handleDeleteClick}
      />

      <ShareWorkflowModal
        isOpen={!!jobToShare}
        onClose={handleShareClose}
        workflowName={jobToShare?.name ?? ""}
        ownerId={jobToShare?.owner_id ?? ""}
        analystsList={analystsList ?? []}
        initialSelectedIds={shareSelectedIds}
        isLoadingInitial={isLoadingShareDetails}
        isSaving={isSavingShare}
        onSave={handleShareSave}
      />

      <ConfirmationModal
        isOpen={!!jobToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Workflow"
        text={`Are you sure you want to delete "${jobToDelete?.name ?? ""}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </>
  );
};

export default JobListContainer;
